import React, { useMemo } from 'react';
import { Copy } from 'lucide-react';
import ReusableTable from '../../../../../common/ReusableTable/ReusableTable';

const formatDisplayDate = (value) => {
  if (!value) return '—';
  const str = String(value);
  const dateStr = str.includes('T') ? str.split('T')[0] : str;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export default function PlannedWorkoutPlansTable({
  tableData,
  loading,
  onSelectDuration,
  onCopyDuration,
  copyingDurationId = null,
  emptyMessage = 'No workout plans yet.',
}) {
  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: 'planName',
        header: 'Plan name',
        size: 200,
        enableSorting: true,
        cell: (info) => (
          <span
            className="text-sm font-medium text-gray-900 truncate block"
            title={info.getValue() || ''}
          >
            {info.getValue() || 'Workout plan'}
          </span>
        ),
      },
      {
        accessorKey: 'start_date',
        header: 'Start date',
        size: 130,
        enableSorting: true,
        cell: (info) => (
          <span className="text-sm text-gray-700">{formatDisplayDate(info.getValue())}</span>
        ),
      },
      {
        accessorKey: 'end_date',
        header: 'End date',
        size: 130,
        enableSorting: true,
        cell: (info) => (
          <span className="text-sm text-gray-700">{formatDisplayDate(info.getValue())}</span>
        ),
      },
      {
        accessorKey: 'weekday_count',
        header: 'Active days',
        size: 100,
        enableSorting: true,
        cell: (info) => <span className="text-sm text-gray-600">{info.getValue() ?? 0}</span>,
      },
    ];

    if (onCopyDuration) {
      cols.push({
        accessorKey: 'copy',
        header: 'Copy',
        size: 90,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const busy =
            copyingDurationId != null &&
            String(copyingDurationId) === String(row.duration_id);
          return (
            <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => onCopyDuration(row)}
                disabled={busy || Boolean(copyingDurationId)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-700 disabled:opacity-50"
                title="Copy plan"
                aria-label="Copy plan"
              >
                <Copy className="w-4 h-4 text-orange-primary" />
                Copy
              </button>
            </div>
          );
        },
      });
    }

    return cols;
  }, [onCopyDuration, copyingDurationId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tableData?.length) {
    return (
      <div className="py-12 text-center rounded-xl border border-gray-200 bg-gray-50">
        <p className="text-gray-600">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <ReusableTable
      columns={columns}
      data={tableData}
      enableSorting
      enableFiltering={false}
      enableResizing
      enablePagination={false}
      onRowClick={onSelectDuration}
      emptyMessage={emptyMessage}
      initialState={{
        sorting: [{ id: 'start_date', desc: true }],
      }}
    />
  );
}
